import React, { useEffect } from "react";

import { FormSelectField } from "@components-shared/mobile/inputs";
import { useTranslation } from "react-i18next";
import { RequiredMark, ErrorMessage } from "@components/inputs/shared";

import { isNullOrWhitespace, checkLeapYear, days, months, years } from "@utils";
import { useFormContext, useFormState, useWatch } from "react-hook-form";
import { DateTime } from "luxon";

export type SowBirthDateProps = {
	label: string;
};

export const SowBirthDateInput = (props: SowBirthDateProps) => {
	const { t } = useTranslation("registration");

	const { getValues, trigger, getFieldState } = useFormContext();
	const { errors } = useFormState();

	useEffect(() => {
		if (
			getFieldState("day").isTouched ||
			getFieldState("month").isTouched ||
			getFieldState("year").isTouched
		) {
			trigger("day");
			trigger("month");
			trigger("year");
		}
	}, [useWatch({ name: ["day", "month", "year"] })]);

	const maxDate = DateTime.now().minus({ years: 18 });
	const minDate = DateTime.now().minus({ years: 100 });

	const sortedYears = years.sort((oldest, newest) => newest.id - oldest.id);

	return (
		<div className="form__field">
			<label htmlFor={"day"} className="label u-type--wgt--medium">
				{t<string>(props.label)}
				<RequiredMark required />
			</label>

			{(errors.day || errors.month || errors.year) && (
				<ErrorMessage
					name={errors.day ? "day" : errors.month ? "month" : "year"}
				/>
			)}
			<div className="row">
				<div className="col col-sml-4">
					<FormSelectField
						name="day"
						placeholder={"registration:DAY"}
						dropdownData={days}
						required
						validate={{
							checkValidDay: (value: string) => {
								const monthValue = getValues("month");
								const yearValue = getValues("year");

								if (
									checkLeapYear(yearValue) === false &&
									parseInt(monthValue) == 2 &&
									parseInt(value) > 28
								) {
									return t<string>("DOB_INVALID");
								}
								if (
									checkLeapYear(yearValue) === true &&
									parseInt(monthValue) == 2 &&
									parseInt(value) > 29
								) {
									return t<string>("DOB_INVALID");
								}
								if (value == "") {
									return t<string>("DOB_INVALID");
								}
							},
							isOldEnough: (value: string) => {
								const monthValue = getValues("month");
								const yearValue = getValues("year");

								if (
									isNullOrWhitespace(monthValue) ||
									isNullOrWhitespace(yearValue)
								) {
									return;
								}

								const dob = DateTime.fromObject({
									day: parseInt(value),
									month: parseInt(monthValue),
									year: parseInt(yearValue),
								});
								if (
									dob.startOf("day") > maxDate.startOf("day")
								) {
									return t<string>("DOB_TOO_YOUNG");
								}
							},
							isTooOld: (value: string) => {
								const monthValue = getValues("month");
								const yearValue = getValues("year");

								if (
									isNullOrWhitespace(monthValue) ||
									isNullOrWhitespace(yearValue)
								) {
									return;
								}

								const dob = DateTime.fromObject({
									day: parseInt(value),
									month: parseInt(monthValue),
									year: parseInt(yearValue),
								});
								if (
									dob.startOf("day") < minDate.startOf("day")
								) {
									return t<string>("DOB_INVALID");
								}
							},
						}}
					/>
				</div>
				<div className="card--membership__col col col-sml-4">
					<FormSelectField
						name="month"
						placeholder={"registration:MONTH"}
						dropdownData={months}
						required
					/>
				</div>
				<div className="card--membership__col col col-sml-4">
					<FormSelectField
						name="year"
						placeholder={"registration:YEAR"}
						dropdownData={sortedYears}
						required
					/>
				</div>
			</div>
		</div>
	);
};
